import React, { useMemo } from 'react';
import { sortBy, last, uniq, keyBy, isEmpty } from 'lodash';
import { addDays, format } from 'date-fns';
import { useLocalStorage, useAsync } from 'react-use';
import { Button, Badge, Modal, ModalHeader, ModalBody } from 'reactstrap';
import { toast } from 'react-toastify';

import firebase from '../firebase';
import { getDocumentData, getCollectionData } from '../shared/firebase';
import { localeProduct } from '../shared/models/product';
import { valueTypes as couponValueTypes, statuses as couponStatuses } from '../shared/models/coupon';

const db = firebase.firestore();
const couponsRef = db.collection('coupons');
const qrUrlsRef = db.collection('qrUrls');

const formatDate = (date) => (date ? format(date, 'yyyy/MM/dd') : '');

// NOTE: 旧方式の savedAt は二重 JSON.stringify で保存されているため JSON.parse するが、
// データ不整合でクラッシュしないよう安全にパースする
const safeParse = (value) => {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch (e) {
    return value;
  }
};

// NOTE: useAvailableReferrerKey と同等のロジックを props の tenant で再現
function useReferrerInfo(tenant) {
  const tenantId = tenant?.id;
  const [referrerKeyInStorage] = useLocalStorage(['referrerKey', tenantId].join('__'));
  const [referrerKeySavedAtString] = useLocalStorage(['referrerKeySavedAt', tenantId].join('__'));
  const [referrers] = useLocalStorage(['referrers', tenantId].join('__'), []);
  const [qrUrlIdInStorage] = useLocalStorage(['qrUrlId', tenantId].join('__'));
  const [qrUrlIdSavedAtString] = useLocalStorage(['qrUrlIdSavedAt', tenantId].join('__'));

  const oldReferrerData = referrerKeyInStorage != null
    ? { referrerKey: referrerKeyInStorage, referrerKeySavedAt: safeParse(referrerKeySavedAtString) }
    : null;
  const availableReferrers = sortBy(
    [...(referrers || []), oldReferrerData]
      .filter((_) => _)
      .map((_) => ({ ..._, referrerKeySavedAt: new Date(_.referrerKeySavedAt) }))
      .filter((_) => _.referrerKeySavedAt > addDays(new Date(), -30)),
    'referrerKeySavedAt'
  );
  const availableReferrer = last(availableReferrers.filter((_) => _.enabledLastReferrerWin)) ?? availableReferrers[0];
  const availableReferrerKey = availableReferrer?.referrerKey ?? null;

  const qrUrlIdSavedAt = qrUrlIdSavedAtString && new Date(safeParse(qrUrlIdSavedAtString));
  const availableQrUrlId = qrUrlIdSavedAt > addDays(new Date(), -30) ? qrUrlIdInStorage : null;

  return {
    availableReferrers,
    availableReferrerKey,
    availableReferrerSavedAt: availableReferrer?.referrerKeySavedAt ?? null,
    availableQrUrlId,
    availableQrUrlSavedAt: qrUrlIdSavedAt || null,
  };
}

function ReferrerInfoContent({ tenant }) {
  const {
    availableReferrers,
    availableReferrerKey,
    availableReferrerSavedAt,
    availableQrUrlId,
    availableQrUrlSavedAt,
  } = useReferrerInfo(tenant);

  // リファラ → referrer/agentShop/agent を解決（Products.jsx と同じ辿り方）
  const { value: referrerInfo, loading: isLoadingReferrer } = useAsync(async () => {
    if (isEmpty(availableReferrerKey)) return null;
    const [referrer] = await getCollectionData(
      db.collectionGroup('referrers').where('key', '==', availableReferrerKey).limit(1)
    );
    if (referrer == null) return { notFound: true };
    const agentShop = await getDocumentData(referrer.ref.parent.parent);
    const agent = await getDocumentData(referrer.ref.parent.parent.parent.parent);
    return { referrer, agentShop, agent };
  }, [availableReferrerKey]);
  const agent = referrerInfo?.agent;

  // QrUrl を解決
  const { value: qrUrl, loading: isLoadingQrUrl } = useAsync(async () => {
    if (isEmpty(availableQrUrlId)) return null;
    return await getDocumentData(qrUrlsRef.doc(availableQrUrlId));
  }, [availableQrUrlId]);

  // 効いているクーポンIDを集約（Products.jsx と同じ）
  const couponIds = useMemo(
    () => uniq([qrUrl?.couponIds, agent?.showingQrUrls?.find((_) => _.qrUrlId === qrUrl?.id)?.couponIds].flatMap((_) => _ || [])),
    [qrUrl, agent]
  );
  const { value: coupons = [], loading: isLoadingCoupons } = useAsync(async () => {
    if (couponIds.length === 0) return [];
    return (await Promise.all(couponIds.map((_) => getDocumentData(couponsRef.doc(_))))).filter((_) => _.exists);
  }, [JSON.stringify(couponIds)]);

  // クーポン対象商品名を引くため商品を取得
  const allProductIds = useMemo(() => uniq(coupons.flatMap((_) => [...(_.productIds || []), ...(_.giftProductIds || [])])), [coupons]);
  const { value: productsById = {} } = useAsync(async () => {
    if (allProductIds.length === 0) return {};
    const products = (await Promise.all(allProductIds.map((_) => getDocumentData(db.collection('products').doc(_))))).filter((_) => _.exists);
    return keyBy(products, 'id');
  }, [JSON.stringify(allProductIds)]);

  const isLoading = isLoadingReferrer || isLoadingQrUrl || isLoadingCoupons;
  const hasReferrer = !isEmpty(availableReferrerKey);
  const hasQrUrl = !isEmpty(availableQrUrlId);
  const hasAnything = hasReferrer || hasQrUrl;

  const productName = (id) => {
    const product = productsById[id];
    if (product == null) return `(商品ID: ${id})`;
    const { name } = localeProduct(tenant, product);
    return `${name}${product.code ? `（${product.code}）` : ''}`;
  };

  return (
    <div>
      {!hasAnything && (
        <div className="alert alert-secondary mb-3">
          現在、効いているリファラ・紹介QR・優待はありません。通常のカート表示です。
        </div>
      )}

      {isLoading && (
        <div className="mb-3">
          <span className="fas fa-spin fa-spinner mr-1" />
          読込中...
        </div>
      )}

      {/* リファラ */}
      {hasReferrer && (
        <section className="mb-4">
          <h6 className="font-weight-bold border-bottom pb-1">
            <span className="fas fa-user-tag mr-1" />
            リファラ
          </h6>
          {referrerInfo?.notFound ? (
            <div className="text-danger small">
              リファラキー「{availableReferrerKey}」に該当するリファラが見つかりませんでした。
            </div>
          ) : (
            <dl className="row mb-0 small">
              <dt className="col-4 col-sm-3 text-muted">リファラ名</dt>
              <dd className="col-8 col-sm-9">{referrerInfo?.referrer?.name || '-'}</dd>
              <dt className="col-4 col-sm-3 text-muted">リファラキー</dt>
              <dd className="col-8 col-sm-9"><code>{availableReferrerKey}</code></dd>
              <dt className="col-4 col-sm-3 text-muted">代理店</dt>
              <dd className="col-8 col-sm-9">{referrerInfo?.agent?.name || '-'}</dd>
              <dt className="col-4 col-sm-3 text-muted">店舗</dt>
              <dd className="col-8 col-sm-9">{referrerInfo?.agentShop?.name || '-'}</dd>
              <dt className="col-4 col-sm-3 text-muted">適用日時</dt>
              <dd className="col-8 col-sm-9">{availableReferrerSavedAt ? format(availableReferrerSavedAt, 'yyyy/MM/dd HH:mm') : '-'}</dd>
            </dl>
          )}
          {availableReferrers.length > 1 && (
            <div className="text-muted small mt-2">
              ほか{availableReferrers.length - 1}件のリファラが保存されています（最新が適用されます）。
            </div>
          )}
        </section>
      )}

      {/* 紹介QR */}
      {hasQrUrl && (
        <section className="mb-4">
          <h6 className="font-weight-bold border-bottom pb-1">
            <span className="fas fa-qrcode mr-1" />
            紹介QR
          </h6>
          <dl className="row mb-0 small">
            <dt className="col-4 col-sm-3 text-muted">QR名</dt>
            <dd className="col-8 col-sm-9">{qrUrl?.name || '-'}</dd>
            <dt className="col-4 col-sm-3 text-muted">リンク先</dt>
            <dd className="col-8 col-sm-9 text-break">{qrUrl?.url || '-'}</dd>
            <dt className="col-4 col-sm-3 text-muted">適用日時</dt>
            <dd className="col-8 col-sm-9">{availableQrUrlSavedAt ? format(availableQrUrlSavedAt, 'yyyy/MM/dd HH:mm') : '-'}</dd>
          </dl>
        </section>
      )}

      {/* 優待（クーポン） */}
      {(coupons.length > 0 || hasAnything) && (
        <section className="mb-4">
          <h6 className="font-weight-bold border-bottom pb-1">
            <span className="fas fa-ticket-alt mr-1" />
            優待
          </h6>
          {coupons.length === 0 ? (
            <div className="text-muted small">効いている優待はありません。</div>
          ) : (
            coupons.map((coupon) => {
              const status = couponStatuses[coupon.status] || {};
              const valueType = couponValueTypes[coupon.valueType] || {};
              const isStarted = coupon.startDate == null || coupon.startDate.toDate() <= new Date();
              const isActive = isStarted && coupon.status !== 'used' && coupon.status !== 'disabled';
              return (
                <div key={coupon.id} className="border rounded p-2 mb-2">
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="font-weight-bold">{coupon.name}</div>
                    <div>
                      <Badge color={status.color || 'secondary'} className="ml-1">{status.label || coupon.status}</Badge>
                      {!isActive && <Badge color="warning" className="ml-1">適用対象外</Badge>}
                    </div>
                  </div>
                  <dl className="row mb-0 small mt-1">
                    <dt className="col-4 col-sm-3 text-muted">割引種別</dt>
                    <dd className="col-8 col-sm-9">
                      {valueType.label || coupon.valueType}
                      {coupon.valueType === 'amount' && coupon.discountAmount != null && `（${coupon.discountAmount.toLocaleString()}円引き）`}
                      {coupon.valueType === 'rate' && coupon.discountRate != null && `（${coupon.discountRate}%引き）`}
                      {coupon.isShipmentFree && '・送料無料'}
                    </dd>
                    {(coupon.startDate || coupon.deadlineDate) && (
                      <>
                        <dt className="col-4 col-sm-3 text-muted">期間</dt>
                        <dd className="col-8 col-sm-9">
                          {formatDate(coupon.startDate?.toDate())} 〜 {formatDate(coupon.deadlineDate?.toDate())}
                          {!isStarted && <span className="text-warning ml-1">（開始前）</span>}
                        </dd>
                      </>
                    )}
                    <dt className="col-4 col-sm-3 text-muted">対象商品</dt>
                    <dd className="col-8 col-sm-9">
                      {isEmpty(coupon.productIds) ? '-' : (
                        <ul className="mb-0 pl-3">
                          {coupon.productIds.map((id) => <li key={id}>{productName(id)}</li>)}
                        </ul>
                      )}
                    </dd>
                    {!isEmpty(coupon.giftProductIds) && (
                      <>
                        <dt className="col-4 col-sm-3 text-muted">プレゼント商品</dt>
                        <dd className="col-8 col-sm-9">
                          <ul className="mb-0 pl-3">
                            {coupon.giftProductIds.map((id) => <li key={id}>{productName(id)}</li>)}
                          </ul>
                        </dd>
                      </>
                    )}
                    {coupon.disabilityMessage && coupon.status === 'disabled' && (
                      <>
                        <dt className="col-4 col-sm-3 text-muted">無効理由</dt>
                        <dd className="col-8 col-sm-9 text-danger">{coupon.disabilityMessage}</dd>
                      </>
                    )}
                  </dl>
                </div>
              );
            })
          )}
        </section>
      )}
    </div>
  );
}

export default function ReferrerInfoModal(props) {
  const { tenant, tenantPath, user, isOpen, onClose } = props;
  const onClickClear = async () => {
    if (!window.confirm('リファラ・紹介QR・優待の適用をクリアします。よろしいですか？')) return;

    [
      'qrUrlId',
      'qrUrlIdSavedAt',
      'referrerKey',
      'referrerKeySavedAt',
      'referrers',
      // NOTE: referralLogsは残す
    ].map((_) => window.localStorage.removeItem([_, tenantPath].join('__')));
    if (user?.email != null) {
      await db.collection('referredEmails').doc(user.email).delete();
    }
    toast.success('リファラをクリアしました。ページを再読み込みします。');
    window.location.reload();
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} scrollable>
      <ModalHeader toggle={onClose}>リファラ内容確認</ModalHeader>
      <ModalBody>
        <ReferrerInfoContent tenant={tenant} />
        <div className="d-flex justify-content-between mt-4">
          <Button color="danger" outline onClick={onClickClear}>
            <span className="fas fa-trash mr-1" />
            リファラをクリア
          </Button>
          <Button className="cancel" onClick={onClose}>閉じる</Button>
        </div>
      </ModalBody>
    </Modal>
  );
}
